/**
 * SpaceToggle.jsx — Scribe-only pill in the TopBar letting them flip between
 * "Scribe" and "Reader" spaces.
 *
 * Enhancement (Feb 2 2026): when the scribe is browsing in Reader space, we
 * poll `/api/writer/activity-pulse` every 90s to surface a red dot + count
 * (new followers in the last 7 days + unacknowledged approvals) so they know
 * something on the scribe side is worth checking without tab-switching
 * constantly. The dot clears as soon as they flip into Writer space.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PenTool, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import {
  getActiveSpace,
  setActiveSpace,
  onSpaceChange,
} from '../utils/spaceContext';
import { authFetch } from '../utils/authUtils';

const POLL_MS = 90_000; // 90s — responsive without being chatty.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SpaceToggle = ({ compact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [space, setSpace] = useState(getActiveSpace());
  const [pulse, setPulse] = useState({ count: 0, display: '' });
  const timerRef = useRef(null);

  useEffect(() => {
    const unsub = onSpaceChange((s) => setSpace(s));
    return unsub;
  }, []);

  // Fetch the activity pulse whenever we're in reader space so the scribe
  // gets a gentle ping to come back. In writer space we clear the dot — they
  // can see the real stats directly on their dashboard.
  const fetchPulse = useCallback(async () => {
    if (space !== 'reader') {
      setPulse({ count: 0, display: '' });
      return;
    }
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/activity-pulse`);
      if (!res.ok) return;
      const d = await res.json();
      setPulse({ count: d.badge_count || 0, display: d.badge_display || '' });
    } catch {
      // Silent — best-effort nudge only.
    }
  }, [space]);

  useEffect(() => {
    fetchPulse();
    if (timerRef.current) clearInterval(timerRef.current);
    if (space === 'reader') {
      timerRef.current = setInterval(fetchPulse, POLL_MS);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [space, fetchPulse]);

  const flip = () => {
    const next = space === 'writer' ? 'reader' : 'writer';
    setActiveSpace(next);
    setSpace(next);
    // Clear the pulse the instant they flip into Writer space — they're
    // about to see everything on the dashboard anyway.
    if (next === 'writer') setPulse({ count: 0, display: '' });
    const neutral = ['/profile', '/library', '/discover', '/premium', '/vault'];
    if (!neutral.some((p) => location.pathname.startsWith(p))) {
      navigate(next === 'writer' ? '/writer-dashboard' : '/dashboard');
    }
    toast.success(next === 'writer' ? 'Switched to Writer space' : 'Switched to Reader space');
  };

  const isWriter = space === 'writer';
  const showBadge = !isWriter && pulse.count > 0;
  const testId = 'space-toggle';

  // Shared badge JSX — tiny pulsing red dot with the count (or "•" if zero
  // for whatever reason — never rendered when count === 0, so no fallback).
  const Badge = () => (
    <span
      data-testid="space-toggle-badge"
      className="relative flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold shadow-[0_0_0_2px_rgba(15,23,42,0.95)]"
      aria-label={`${pulse.count} new writer updates`}
    >
      {pulse.display}
      <span className="absolute inset-0 rounded-full bg-rose-500/60 animate-ping" />
    </span>
  );

  if (compact) {
    return (
      <button
        type="button"
        onClick={flip}
        data-testid={testId}
        data-has-activity={showBadge ? 'true' : 'false'}
        className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
          isWriter
            ? 'bg-purple-600/20 text-purple-200 border-purple-500/50 hover:bg-purple-600/30'
            : 'bg-emerald-600/20 text-emerald-200 border-emerald-500/50 hover:bg-emerald-600/30'
        }`}
        aria-label={`Switch to ${isWriter ? 'Reader' : 'Scribe'} space${showBadge ? `, ${pulse.count} new updates` : ''}`}
      >
        {isWriter ? <PenTool className="w-3 h-3" /> : <BookOpen className="w-3 h-3" />}
        <span className="hidden sm:inline">{isWriter ? 'Scribe' : 'Reader'}</span>
        <span className="text-gray-400">⇄</span>
        {showBadge && (
          <span className="absolute -top-1.5 -right-1.5">
            <Badge />
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={flip}
      data-testid={testId}
      data-has-activity={showBadge ? 'true' : 'false'}
      className={`relative w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg font-semibold text-sm border transition-colors ${
        isWriter
          ? 'bg-purple-600/20 text-purple-200 border-purple-500/40 hover:bg-purple-600/30'
          : 'bg-emerald-600/20 text-emerald-200 border-emerald-500/40 hover:bg-emerald-600/30'
      }`}
    >
      <span className="flex items-center gap-2">
        {isWriter ? <PenTool className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
        Active: {isWriter ? 'Scribe' : 'Reader'} space
      </span>
      <span className="flex items-center gap-2 text-xs text-gray-300">
        {showBadge && <Badge />}
        Switch ⇄
      </span>
    </button>
  );
};

export default SpaceToggle;
